import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import {
  APPLICANT_STAGE_LABELS,
  recordNoun,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STEPS,
  type AllotmentCycleUsage,
  type ReferralSummary,
} from '@hv/shared';
import { useDeleteReferral } from '../api/hooks';
import { useNotify } from '../components/Notify';
import { referralCardActions } from './card-actions';

const familyLabel = (r: ReferralSummary) =>
  r.familyLastName
    ? `${r.familyLastName} family`
    : `Untitled ${recordNoun(r.countsTowardAllotment).toLowerCase()}`;

function ReferralCard({ referral, onDelete }: { referral: ReferralSummary; onDelete: () => void }) {
  const draft = referral.status === 'draft';
  const details = [referral.cycle?.label, referral.childNames.join(', ')]
    .filter(Boolean)
    .join(' · ');
  return (
    <Paper
      component="article"
      aria-label={familyLabel(referral)}
      sx={{ p: 2 }}
      data-testid="referral-card"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h3" component="h3">
              {familyLabel(referral)}
            </Typography>
            <Chip
              size="small"
              label={REFERRAL_STATUS_LABELS[referral.status]}
              color={draft ? 'default' : 'primary'}
            />
            {referral.countsTowardAllotment && (
              <Chip size="small" variant="outlined" color="secondary" label="Recommendation" />
            )}
          </Stack>
          {details && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {details}
            </Typography>
          )}
          {!draft && referral.childStages.length > 0 && (
            <Stack
              direction="row"
              gap={0.5}
              flexWrap="wrap"
              mt={0.75}
              aria-label="Applicant stages"
            >
              {referral.childStages.map((c, i) => (
                <Chip
                  key={c.childKey}
                  size="small"
                  variant="outlined"
                  label={`${c.name || `Child ${i + 1}`}: ${APPLICANT_STAGE_LABELS[c.stage]}`}
                  data-testid="child-stage"
                />
              ))}
            </Stack>
          )}
          {referral.clarification && (
            <Alert severity="warning" sx={{ mt: 1 }} data-testid="card-clarification">
              <Typography variant="body2" fontWeight={600}>
                Admissions asked for clarification on {new Date(referral.clarification.requestedAt).toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {referral.clarification.request}
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {draft
              ? `Step ${referral.currentStep} of ${REFERRAL_STEPS.length}: ${REFERRAL_STEPS[referral.currentStep - 1]?.label} · Last saved ${new Date(referral.updatedAt).toLocaleString()}`
              : `Reference ${referral.referenceNumber ?? '—'} · Submitted ${referral.submittedAt ? new Date(referral.submittedAt).toLocaleString() : '—'}`}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexShrink={0}>
          {referralCardActions(referral).map((a) =>
            a.id === 'delete' ? (
              <Button key={a.id} color="error" onClick={onDelete}>
                {a.label}
              </Button>
            ) : (
              <Button
                key={a.id}
                variant={a.id === 'continue' ? 'contained' : a.id === 'edit' ? 'outlined' : 'text'}
                component={RouterLink}
                to={a.to!}
              >
                {a.label}
              </Button>
            ),
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function ReferralCardList({ rows, empty }: { rows: ReferralSummary[]; empty: string }) {
  const remove = useDeleteReferral();
  const { notify, notifyError } = useNotify();
  const [deleting, setDeleting] = useState<ReferralSummary | null>(null);

  return (
    <>
      {rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{empty}</Typography>
        </Paper>
      ) : (
        <Stack gap={2}>
          {rows.map((r) => (
            <ReferralCard key={r.id} referral={r} onDelete={() => setDeleting(r)} />
          ))}
        </Stack>
      )}
      <Dialog open={deleting !== null} onClose={() => !remove.isPending && setDeleting(null)}>
        <DialogTitle>Delete this draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The draft for {deleting ? familyLabel(deleting) : ''} will be permanently deleted. This
            can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() =>
              deleting &&
              remove.mutate(deleting.id, {
                onSuccess: () => {
                  setDeleting(null);
                  notify('success', 'Draft deleted');
                },
                onError: (err) => notifyError(err, 'Deleting the draft failed'),
              })
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export const currentUsage = (cycles: readonly AllotmentCycleUsage[]) =>
  cycles.filter((c) => !c.ended && (c.cycle.referralsOpen || c.used > 0));

export function AllotmentUsageList({
  cycles,
  limit,
}: {
  cycles: readonly AllotmentCycleUsage[];
  limit: number;
}) {
  const shown = currentUsage(cycles);
  if (!shown.length)
    return (
      <Typography variant="body2" color="text.secondary">
        No academic year is open to trustee referrals right now.
      </Typography>
    );
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }} data-testid="allotment-usage">
      {shown.map((c) => (
        <Stack
          component="li"
          key={c.cycle.id}
          direction="row"
          justifyContent="space-between"
          gap={2}
          py={0.5}
        >
          <Typography variant="body2" color="text.secondary">
            {c.cycle.label}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {c.used} of {limit} in use
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}
